import { ReactNode, useContext, useState } from 'react';
// Microsoft
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Modal, Spinner } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
// Icons
import { BookOpen, GridFour, Video, X, Globe } from '@phosphor-icons/react';
// Styles
import { modalStyles } from '../AppModal/styles';
import {
  ReadinessCardContainer,
  ReadinessCardContent,
  ReadinessModalContent,
  ReadinessModalHeader,
} from './styles';
import { Skeleton } from '../../../styles/components';
import { Tag } from '../Tag';
import { TeamsFxContext } from '../../Context';

interface ReadinessCardProps {
  data?: ReadinessObject;
  isMobile?: boolean;
  index?: number;
}

enum ReadinessType {
  Video = 'video',
  Doc = 'doc',
}

export interface ReadinessObject {
  id: string;
  title: string;
  description: string;
  category: string;
  type: ReadinessType;
  contentUrl: string;
  img: string;
  tag: string;
  tagColor: string;
}

export const ReadinessCard = ({
  data,
  isMobile,
  index,
}: ReadinessCardProps) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const context = useContext(TeamsFxContext);

  const titleId = useId('title');
  const appInsights = useAppInsightsContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  function openModal() {
    if (process.env.REACT_APP_APP_INSIGHTS !== '') {
      appInsights.trackEvent({
        name: 'Readiness Card Opened',
        properties: {
          cardName: data?.title,
        },
      });
    }
    setIsModalOpen(true);
  }

  function hideModal() {
    setIsModalOpen(false);
    setIsIframeLoaded(false);
  }

  const getIcon = (type: ReadinessType): ReactNode => {
    switch (type) {
      case ReadinessType.Doc:
        return <BookOpen weight="duotone" />;
      case ReadinessType.Video:
        return <Video weight="duotone" />;
      default:
        return <GridFour weight="duotone" />;
    }
  };

  const doctheme =
    data?.contentUrl +
    '?theme=' +
    (context?.themeString === 'default' ? 'black' : 'white');

  return (
    <ReadinessCardContainer
      tagColor={data?.tagColor ?? ''}
      onClick={() => openModal()}
      id={index === 0 ? 'step-learning-and-resources' : ''}
    >
      {data && (
        <div className="tagWrapper">
          <Tag
            color={data.tagColor}
            content={data.tag}
            variant="outlined"
            compact
          />
        </div>
      )}
      <ReadinessCardContent>
        <div className="iconWrapper">{data && getIcon(data.type)}</div>
        {data ? (
          <>
            <h1>{data.title}</h1>
            <p>{data.description}</p>
          </>
        ) : (
          <>
            <Skeleton height={1} />
            <Skeleton height={0.9} />
          </>
        )}
      </ReadinessCardContent>
      <Modal
        titleAriaId={titleId}
        isOpen={isModalOpen}
        onDismiss={hideModal}
        isBlocking={false}
        containerClassName={modalStyles.container}
      >
        {!data ? (
          <Spinner size={3} label="Loading content..." />
        ) : (
          <>
            <ReadinessModalHeader>
              <h1 id={titleId}>{data.title}</h1>
              <button className="closeModal" onClick={hideModal}>
                <X aria-label="Close popup modal" size={24} />
              </button>
            </ReadinessModalHeader>
            <ReadinessModalContent>
              {!isMobile && data.type === 'doc' && (
                <a
                  href={data.contentUrl}
                  className="contentLink"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Globe />
                </a>
              )}
              <p>{data.description}</p>

              {data.type === 'video' && (
                <div className="videoContainer">
                  <div
                    className={`${
                      isIframeLoaded ? 'loaded ' : ''
                    } iframeWrapper`}
                  >
                    <img
                      src="https://app.bitfractal.com/embedded/maxresdefault.jpg"
                      className="thumbnail"
                      alt=""
                    />
                    <iframe
                      title="BitFractal Readiness Video"
                      src={data.contentUrl}
                      allow={'autoplay'}
                      allowFullScreen
                      height={400}
                      width={711.11}
                      onLoad={() => setIsIframeLoaded(true)}
                    />
                  </div>
                </div>
              )}
              {data.type === 'doc' && (
                <iframe src={doctheme} title={data.title}></iframe>
              )}
            </ReadinessModalContent>
          </>
        )}
      </Modal>
    </ReadinessCardContainer>
  );
};
